import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import ReactTable from 'react-table';
import 'react-table/react-table.css';
import Select, { makeAnimated } from 'react-select';
import { AsyncTypeahead, Highlighter } from 'react-bootstrap-typeahead';
import { Configurations } from '../../Utils/Configurations';
import ErrorModal from '../../Components/ErrorModal/ErrorModal';
import SuccessModal from '../../Components/SuccessModal/SuccessModal';
import './Article-Preview-Users.scss';

// Permissões de preview (campo `permissoes` da API): cada número corresponde a um produto.
export const PERMISSOES_PREVIEW = [
    { label: 'BS', value: 1 },
    { label: 'TC', value: 2 },
    { label: 'RD', value: 3 },
    { label: 'GPV', value: 4 },
    { label: 'BM', value: 5 },
];

const ArticlePreviewUsers = () => {
    const configurations = new Configurations();
    const typeaheadRef = useRef(null);

    const [usuarios, setUsuarios] = useState([]);
    const [isLoadingUsuarios, setIsLoadingUsuarios] = useState(true);
    const [paginaAtual, setPaginaAtual] = useState(1);
    const [totalPaginas, setTotalPaginas] = useState(0);
    const [limite] = useState(20);
    const [opcoesBusca, setOpcoesBusca] = useState([]);
    const [isLoadingBusca, setIsLoadingBusca] = useState(false);
    const [mensagemBusca, setMensagemBusca] = useState('');
    const [idsSalvando, setIdsSalvando] = useState([]);
    const [idsRemovendo, setIdsRemovendo] = useState([]);
    const [hasError, setHasError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [hasSuccess, setHasSuccess] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    const handleError = (err) => {
        console.error('Erro na gestão de usuários do preview', err);
        setHasError(true);
        setErrorMessage(err.response && err.response.data && err.response.data.message ? err.response.data.message : 'Erro inesperado.');
    };

    const closeErrorModal = () => {
        setHasError(false);
        setErrorMessage('');
    };

    // POST e PUT ainda não devolvem `message`; nesses casos usa a mensagem padrão.
    const handleSuccess = (response, mensagemPadrao) => {
        setHasSuccess(true);
        setSuccessMessage(response.data && response.data.message ? response.data.message : mensagemPadrao);
    };

    const closeSuccessModal = () => {
        setHasSuccess(false);
        setSuccessMessage('');
    };

    // `permissoes` é o que está na tela; `permissoesSalvas` é o último estado salvo na API.
    const formataUsuarioSalvo = (usuario) => ({
        ...usuario,
        permissoes: usuario.permissoes || [],
        permissoesSalvas: usuario.permissoes || [],
        isNovo: false,
    });

    const carregarUsuarios = (pagina) => {
        setIsLoadingUsuarios(true);
        axios.get(`${configurations.apiBasePath}/conteudo/preview/usuarios?pagina=${pagina}&limite=${limite}`)
            .then(response => {
                const lista = response.data.usuarios || [];
                setUsuarios(lista.map(formataUsuarioSalvo));
                setTotalPaginas((response.data.paginacao && response.data.paginacao.totalPaginas) || 0);
                setPaginaAtual(pagina);
            })
            .catch(handleError)
            .finally(() => setIsLoadingUsuarios(false));
    };

    useEffect(() => {
        carregarUsuarios(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onPageChange = (pageIndex) => carregarUsuarios(pageIndex + 1);

    const buscarUsuarios = (query) => {
        setIsLoadingBusca(true);
        axios.get(`${configurations.apiBasePath}/busca?query=${query}`)
            .then(response => {
                setOpcoesBusca((response.data && response.data.users) || []);
            })
            .catch(handleError)
            .finally(() => setIsLoadingBusca(false));
    };

    const usuarioJaAdicionado = (idUsuario) => usuarios.some(u => u.idUsuario === idUsuario);

    const temAlteracao = (usuario) => {
        const atuais = [...usuario.permissoes].sort().join(',');
        const salvas = [...usuario.permissoesSalvas].sort().join(',');
        return atuais !== salvas;
    };

    // O usuário selecionado na busca só entra na tabela; a API é chamada ao salvar.
    const adicionarUsuario = (selecionados) => {
        if (!selecionados || selecionados.length === 0) return;

        const usuario = selecionados[0];
        if (typeaheadRef.current) typeaheadRef.current.getInstance().clear();

        if (!usuario.idUsuario) {
            setMensagemBusca('Este resultado não possui usuário vinculado.');
            return;
        }
        if (usuarioJaAdicionado(usuario.idUsuario)) {
            setMensagemBusca(`${usuario.nome} já está na lista.`);
            return;
        }

        setMensagemBusca('');
        setUsuarios(atual => [...atual, {
            idUsuario: usuario.idUsuario,
            nome: usuario.nome,
            codMembro: usuario.codMembro,
            divisao: usuario.divisao,
            permissoes: [],
            permissoesSalvas: [],
            isNovo: true,
        }]);
    };

    const alterarPermissoes = (idUsuario, selecionados) => {
        const permissoes = (selecionados || []).map(item => item.value);
        setUsuarios(atual => atual.map(u => (u.idUsuario === idUsuario ? { ...u, permissoes } : u)));
    };

    // Usuário novo: POST com idUsuario e permissoes. Usuário existente: PUT com a lista final de permissoes.
    const salvarUsuario = (usuario) => {
        const { idUsuario, permissoes, isNovo } = usuario;
        setIdsSalvando(atual => [...atual, idUsuario]);

        const requisicao = isNovo
            ? axios.post(`${configurations.apiBasePath}/conteudo/preview/usuarios`, { idUsuario, permissoes })
            : axios.put(`${configurations.apiBasePath}/conteudo/preview/usuarios/${idUsuario}`, { permissoes });

        requisicao
            .then(response => {
                const usuarioSalvo = formataUsuarioSalvo(response.data.usuario);
                setUsuarios(atual => atual.map(u => (u.idUsuario === idUsuario ? { ...u, ...usuarioSalvo } : u)));
                handleSuccess(response, isNovo ? 'Usuário adicionado com sucesso.' : 'Permissões atualizadas com sucesso.');
            })
            .catch(handleError)
            .finally(() => setIdsSalvando(atual => atual.filter(id => id !== idUsuario)));
    };

    // Usuário novo ainda não foi salvo, então só sai da tabela.
    // Usuário existente: DELETE faz soft delete (desativa todas as permissões).
    const removerUsuario = (usuario) => {
        const { idUsuario, isNovo } = usuario;

        if (isNovo) {
            setUsuarios(atual => atual.filter(u => u.idUsuario !== idUsuario));
            return;
        }

        setIdsRemovendo(atual => [...atual, idUsuario]);
        axios.delete(`${configurations.apiBasePath}/conteudo/preview/usuarios/${idUsuario}`)
            .then(response => {
                setUsuarios(atual => atual.filter(u => u.idUsuario !== idUsuario));
                handleSuccess(response, 'Usuário removido com sucesso.');
            })
            .catch(handleError)
            .finally(() => setIdsRemovendo(atual => atual.filter(id => id !== idUsuario)));
    };

    const columns = [
        {
            Header: 'Nome',
            accessor: 'nome',
            className: 'text-left',
            Cell: row => (
                <React.Fragment>
                    {row.value}
                    {!!row.original.codMembro && <small className="text-muted">&nbsp;(M{row.original.codMembro})</small>}
                    {!!row.original.divisao && <span className="badge badge-info ml-2">{row.original.divisao}</span>}
                    {row.original.isNovo && <span className="badge badge-warning ml-2">não salvo</span>}
                </React.Fragment>
            ),
        },
        {
            Header: 'Acesso',
            accessor: 'permissoes',
            className: 'text-left produtos-preview',
            width: 400,
            sortable: false,
            Cell: row => (
                <Select
                    placeholder="Selecione os produtos"
                    closeMenuOnSelect={false}
                    components={makeAnimated()}
                    isMulti
                    isDisabled={idsSalvando.indexOf(row.original.idUsuario) >= 0 || idsRemovendo.indexOf(row.original.idUsuario) >= 0}
                    value={PERMISSOES_PREVIEW.filter(p => row.value.indexOf(p.value) >= 0)}
                    onChange={selecionados => alterarPermissoes(row.original.idUsuario, selecionados)}
                    options={PERMISSOES_PREVIEW}
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                />
            ),
        },
        {
            Header: 'Ações',
            className: 'text-center',
            width: 230,
            sortable: false,
            Cell: row => {
                const isSalvando = idsSalvando.indexOf(row.original.idUsuario) >= 0;
                const isRemovendo = idsRemovendo.indexOf(row.original.idUsuario) >= 0;
                const semPermissoes = row.original.permissoes.length === 0;
                return (
                    <React.Fragment>
                        {(row.original.isNovo || temAlteracao(row.original) || isSalvando) && (
                            <button
                                className="btn btn-sm btn-outline-success mr-2"
                                disabled={isSalvando || isRemovendo || semPermissoes}
                                title={semPermissoes ? 'Selecione ao menos um produto' : ''}
                                onClick={() => salvarUsuario(row.original)}
                            >
                                {isSalvando ? 'salvando...' : 'salvar'}
                            </button>
                        )}
                        <button className="btn btn-sm btn-outline-danger" disabled={isSalvando || isRemovendo} onClick={() => removerUsuario(row.original)}>
                            {isRemovendo ? 'removendo...' : 'remover'}
                        </button>
                    </React.Fragment>
                );
            },
        },
    ];

    return (
        <div className="row article-preview-users">
            <div className="col-12">
                <h2>Usuários com acesso ao preview</h2>
                <div className="row align-items-center py-1">
                    <div className="col-12 col-lg-6 mt-2">
                        <div className="form-group">
                            <label htmlFor="buscaUsuarioPreview">Adicionar usuário</label>
                            <AsyncTypeahead
                                id="buscaUsuarioPreview"
                                ref={typeaheadRef}
                                isLoading={isLoadingBusca}
                                className="ebs-typeahead w-100"
                                onChange={adicionarUsuario}
                                onInputChange={() => setMensagemBusca('')}
                                labelKey={option => option.nome || ''}
                                renderMenuItemChildren={(option, props) => (
                                    <div className="d-flex align-items-center border-bottom">
                                        <span className="badge badge-info">{option.divisao ? option.divisao : 'US'}</span>
                                        &nbsp;
                                        <Highlighter search={props.text}>{option.nome || ''}</Highlighter>
                                        &nbsp;<small>(
                                            <Highlighter search={props.text.substr(1, props.text.length)}>
                                                {option.codMembro ? option.codMembro.toString() : 'Não membro'}
                                            </Highlighter>)</small>
                                        {usuarioJaAdicionado(option.idUsuario) && <small className="text-muted ml-2">já adicionado</small>}
                                    </div>
                                )}
                                delay={400}
                                emptyLabel="Nenhum resultado encontrado"
                                searchText="Procurando..."
                                promptText="Digite para procurar..."
                                minLength={2}
                                placeholder="Insira o nome ou código de membro..."
                                onSearch={buscarUsuarios}
                                options={opcoesBusca}
                                filterBy={() => true}
                            />
                            {mensagemBusca
                                ? <small className="text-danger">{mensagemBusca}</small>
                                : <small>Selecione um usuário nos resultados para adicioná-lo à lista.</small>}
                        </div>
                    </div>
                </div>
                <div className="row mt-3">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-body p-0">
                                <ReactTable
                                    data={usuarios}
                                    columns={columns}
                                    manual
                                    pages={totalPaginas}
                                    page={paginaAtual - 1}
                                    pageSize={limite}
                                    onPageChange={onPageChange}
                                    showPageSizeOptions={false}
                                    loading={isLoadingUsuarios}
                                    className="-striped -highlight"
                                    headerClassName="ebs-table-header"
                                    previousText="Anterior"
                                    nextText="Próximo"
                                    loadingText="Carregando..."
                                    noDataText="Nenhum usuário com acesso ao preview."
                                    pageText="Página"
                                    ofText="de"
                                    rowsText="linhas"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <ErrorModal
                isOpen={hasError}
                message={errorMessage}
                closeErrorModal={closeErrorModal}
                goBack={false}
            />

            <SuccessModal
                isOpen={hasSuccess}
                message={successMessage}
                closeSuccessModal={closeSuccessModal}
            />
        </div>
    );
};

export default ArticlePreviewUsers;
